// eslint-disable-next-line
import React, { Component } from 'react';
import * as MuiCore from '@material-ui/core';
import * as Icons from '@material-ui/icons';
import TransferRecipientForm from './TransferRecipientForm';
import { WarmTransferForm } from './WarmTransferForm';

const makeRecipient = (name = '', phone = '', isPhoneValid = false) => ({
    id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now() + Math.random()),
    name,
    phone,
    isPhoneValid,
    nameError: '',
    phoneError: '',
});

/**
 * AI Actions Management Card - POI Style
 * Three separate sections: Transfer Conditions, SMS Actions, Email Actions
 */
class AIActionsCard extends Component {
    state = {
        // Transfer Conditions
        showTransferForm: false,
        editingTransfer: null,
        transferCondition: '',
        transferRecipients: [],
        transferErrors: {},
        
        // SMS Actions
        showSmsForm: false,
        editingSms: null,
        smsDataName: '',
        smsDataContent: '',
        smsErrors: {},
        
        // Email Actions
        showEmailForm: false,
        editingEmail: null,
        emailName: '',
        emailSubject: '',
        emailBody: '',
        emailErrors: {},

        // Menu state
        anchorEl: null,
        menuAction: null
    };

    // ====================
    // TRANSFER CONDITIONS
    // ====================
    
    handleAddTransfer = () => {
    this.setState({
        showTransferForm: true,
        editingTransfer: null,
        transferCondition: '',
        transferRecipients: [makeRecipient()],
        transferErrors: {}
    });
    };

    handleEditTransfer = (action) => {
    let recipients = [makeRecipient()];
    try {
        if (action.parameters) {
            const parsed = JSON.parse(action.parameters);
            const entries = Object.entries(parsed);
            if (entries.length > 0) {
                recipients = entries.map(([name, phone]) => makeRecipient(name, phone, true));
            }
        }
    } catch (e) {
        console.error('Error parsing transfer recipients:', e);
    }

    this.setState({
        showTransferForm: true,
        editingTransfer: action,
        transferCondition: action.description || '',
        transferRecipients: recipients,
        transferErrors: {}
    });
};

    handleSaveTransfer = () => {
    const { accountId, onSave } = this.props;
    const { editingTransfer, transferCondition, transferRecipients } = this.state;

    // Top-level: condition required
    if (!transferCondition || transferCondition.trim() === '') {
        this.setState({ transferErrors: { condition: 'Transfer condition is required' } });
        return;
    }

    // Per-recipient validation
    let hasError = false;
    const validatedRecipients = transferRecipients.map((r) => {
        const nameError = !r.name || !r.name.trim() ? 'Name is required' : '';
        let phoneError = '';
        if (!r.phone || !r.phone.trim()) {
            phoneError = 'Phone number is required';
        } else if (!r.isPhoneValid) {
            phoneError = 'Phone number is invalid';
        }
        if (nameError || phoneError) hasError = true;
        return { ...r, nameError, phoneError };
    });

    if (hasError) {
        this.setState({
            transferRecipients: validatedRecipients,
            transferErrors: { recipients: 'Please fix the errors below' }
        });
        return;
    }

    this.setState({ transferErrors: {} });

    // Convert to {name: phone}
    const recipientsObj = {};
    validatedRecipients.forEach((r) => {
        recipientsObj[r.name.trim()] = r.phone.trim();
    });

    const aiAction = {
        actionName: editingTransfer ? editingTransfer.actionName : 'NEW TRANSFER CONDITION',
        actionType: 'TRANSFER_CONDITION',
        channel: 'VOICE',
        status: editingTransfer ? editingTransfer.status : 'NEW',
        description: transferCondition,
        parameters: JSON.stringify(recipientsObj),
        accountId: accountId
    };

    if (editingTransfer) {
        aiAction.aiActionId = editingTransfer.aiActionId;
    }

    onSave(aiAction, editingTransfer !== null);
    this.handleCancelTransfer();
};

    handleCancelTransfer = () => {
    this.setState({
        showTransferForm: false,
        editingTransfer: null,
        transferCondition: '',
        transferRecipients: [makeRecipient()],
        transferErrors: {}
    });
};

    handleAddRecipient = () => {
    this.setState((prev) => ({
        transferRecipients: [...prev.transferRecipients, makeRecipient()]
    }));
};


    handleRemoveRecipient = (id) => {
    this.setState((prev) => {
        const filtered = prev.transferRecipients.filter((r) => r.id !== id);
        return {
            transferRecipients: filtered.length > 0 ? filtered : [makeRecipient()]
        };
    });
};

    handleRecipientChange = (id, field, value) => {
    this.setState((prev) => ({
        transferRecipients: prev.transferRecipients.map((r) =>
            r.id === id ? { ...r, [field]: value, nameError: '', phoneError: '' } : r
        )
    }));
};


    // ====================
    // SMS ACTIONS
    // ====================
    
    handleAddSms = () => {
        this.setState({
            showSmsForm: true,
            editingSms: null,
            smsDataName: '',
            smsDataContent: ''
        });
    };

    handleEditSms = (action) => {
        this.setState({
            showSmsForm: true,
            editingSms: action,
            smsDataName: action.actionName || '',
            smsDataContent: action.description || ''
        });
    };

    handleSaveSms = () => {
        const { accountId, onSave } = this.props;
        const { editingSms, smsDataName, smsDataContent } = this.state;

        // Validation: Check if name and content are filled
        const errors = {};
        if (!smsDataName || smsDataName.trim() === '') {
            errors.name = 'SMS action name is required';
        }
        if (!smsDataContent || smsDataContent.trim() === '') {
            errors.content = 'SMS content is required';
        }

        if (Object.keys(errors).length > 0) {
            this.setState({ smsErrors: errors });
            return;
        }

        // Clear errors
        this.setState({ smsErrors: {} });

        const aiAction = {
            actionName: smsDataName,
            actionType: 'CUSTOM_CONTENT',
            channel: 'SMS',
            status: editingSms ? editingSms.status : 'NEW',
            description: smsDataContent,
            parameters: '',
            accountId: accountId
        };

        if (editingSms) {
            aiAction.aiActionId = editingSms.aiActionId;
        }

        onSave(aiAction, editingSms !== null);
        this.handleCancelSms();
    };

    handleCancelSms = () => {
        this.setState({
            showSmsForm: false,
            editingSms: null,
            smsDataName: '',
            smsDataContent: '',
            smsErrors: {}
        });
    };

    // ====================
    // EMAIL ACTIONS
    // ====================
    
    handleAddEmail = () => {
        this.setState({
            showEmailForm: true,
            editingEmail: null,
            emailName: '',
            emailSubject: '',
            emailBody: ''
        });
    };

    handleEditEmail = (action) => {
        let subject = '';
        let body = '';
        try {
            if (action.parameters) {
                const parsed = JSON.parse(action.parameters);
                subject = parsed.subject || '';
            }
        } catch (e) {
            console.error('Error parsing email parameters:', e);
        }
        body = action.description || '';

        this.setState({
            showEmailForm: true,
            editingEmail: action,
            emailName: action.actionName || '',
            emailSubject: subject,
            emailBody: body
        });
    };

    handleSaveEmail = () => {
        const { accountId, onSave } = this.props;
        const { editingEmail, emailName, emailSubject, emailBody } = this.state;

        // Validation: Check if name, subject and body are filled
        const errors = {};
        if (!emailName || emailName.trim() === '') {
            errors.name = 'Email action name is required';
        }
        if (!emailSubject || emailSubject.trim() === '') {
            errors.subject = 'Email subject is required';
        }
        if (!emailBody || emailBody.trim() === '') {
            errors.body = 'Email body is required';
        }

        if (Object.keys(errors).length > 0) {
            this.setState({ emailErrors: errors });
            return;
        }

        // Clear errors
        this.setState({ emailErrors: {} });

        const aiAction = {
            actionName: emailName,
            actionType: 'CUSTOM_CONTENT',
            channel: 'EMAIL',
            status: editingEmail ? editingEmail.status : 'NEW',
            description: emailBody,
            parameters: JSON.stringify({ subject: emailSubject }),
            accountId: accountId
        };

        if (editingEmail) {
            aiAction.aiActionId = editingEmail.aiActionId;
        }

        onSave(aiAction, editingEmail !== null);
        this.handleCancelEmail();
    };

    handleCancelEmail = () => {
        this.setState({
            showEmailForm: false,
            editingEmail: null,
            emailName: '',
            emailSubject: '',
            emailBody: '',
            emailErrors: {}
        });
    };

    // ====================
    // ALL
    // ====================

    handleMenuOpen = (event, action) => {
        this.setState({
            anchorEl: event.currentTarget,
            menuAction: action
        });
    };

    handleMenuClose = () => {
        this.setState({
            anchorEl: null,
            menuAction: null
        });
    };

    handleMenuEdit = () => {
        const { menuAction } = this.state;
        if (!menuAction) return;

        // Determine which type and call appropriate edit handler
        if (menuAction.actionType === 'TRANSFER_CONDITION') {
            this.handleEditTransfer(menuAction);
        } else if (menuAction.channel === 'SMS') {
            this.handleEditSms(menuAction);
        } else if (menuAction.channel === 'EMAIL') {
            this.handleEditEmail(menuAction);
        }
        this.handleMenuClose();
    };

    handleMenuToggle = () => {
        const { menuAction } = this.state;
        if (!menuAction) return;

        this.handleToggleStatus(menuAction);
        this.handleMenuClose();
    };

    handleToggleStatus = (action) => {
        const { onSave } = this.props;
        const updatedAction = {
            ...action,
            status: action.status === 'ACTIVE' ? 'DISABLE' : 'ACTIVE'
        };
        onSave(updatedAction, true);
    };

    parseTransferRecipients = (parametersJson) => {
        try {
            if (!parametersJson) return '';
            const params = JSON.parse(parametersJson);
            return Object.keys(params).join(', ');
        } catch (e) {
            return '';
        }
    };

    render() {
        const { classes, aiActions, loading, warmTransferEnabled, accountId } = this.props;
        // Filter actions by type
        const transferActions = aiActions.filter(a => a.actionType === 'TRANSFER_CONDITION' && a.channel === 'VOICE');
        const smsActions = aiActions.filter(a => a.actionType === 'CUSTOM_CONTENT' && a.channel === 'SMS');
        const emailActions = aiActions.filter(a => a.actionType === 'CUSTOM_CONTENT' && a.channel === 'EMAIL');
        return (
            <MuiCore.Box>
                {/* Warm Transfer Feature Status */}

                {/* Loading State */}
                {loading && (
                    <MuiCore.Box style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                        <MuiCore.CircularProgress />
                    </MuiCore.Box>
                )}

                {!loading && (
                    <>
                        {/* =============== TRANSFER CONDITIONS SECTION =============== */}
                        <MuiCore.Box className={classes.section} style={{ marginBottom: 32 }}>
                            <MuiCore.Box style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                                <Icons.PhoneForwarded style={{ marginRight: 8, color: '#7F2062' }} />
                                <MuiCore.Typography variant="h6" style={{ fontFamily: 'Sofia-bold', flex: 1, color: '#7F2062' }}>
                                    Transfer Conditions
                                </MuiCore.Typography>
                                <MuiCore.Button
                                    variant="outlined"
                                    size="small"
                                    onClick={this.handleAddTransfer}
                                    startIcon={<Icons.Add />}
                                    style={{ textTransform: 'none', fontFamily: 'Sofia' }}
                                >
                                    ADD
                                </MuiCore.Button>
                            </MuiCore.Box>
                            <MuiCore.Typography variant="body2" color="textSecondary" style={{ marginBottom: 16, fontFamily: 'Sofia' }}>
                                Your Grasshopper AI will transfer callers to the designated phone number when specified conditions are met
                            </MuiCore.Typography>
                            {this.state.showTransferForm ? (
                                <MuiCore.Paper style={{ padding: 24, marginBottom: 16 }}>
                                    <MuiCore.Typography variant="h6" style={{ marginBottom: 16, fontFamily: 'Sofia-bold' }}>
                                        {this.state.editingTransfer ? 'Edit Transfer Condition' : 'Create Transfer Condition'}
                                    </MuiCore.Typography>

                                    <MuiCore.TextField
                                        value={this.state.transferCondition}
                                        onChange={(e) => this.setState({ transferCondition: e.target.value, transferErrors: {} })}
                                        variant="outlined"
                                        fullWidth
                                        style={{ marginBottom: 24 }}
                                        placeholder="Please enter your condition"
                                        error={!!this.state.transferErrors.condition}
                                        helperText={this.state.transferErrors.condition || ''}
                                        inputProps={{ style: { fontFamily: 'Sofia' } }}
                                        InputLabelProps={{ style: { fontFamily: 'Sofia' } }}
                                        InputProps={{ style: { fontFamily: 'Sofia' } }}
                                    />

                                   {this.state.transferRecipients.map((recipient, index) => (
                                        <TransferRecipientForm
                                            key={recipient.id}
                                            recipient={recipient}
                                            index={index}
                                            onChange={this.handleRecipientChange}
                                            onRemove={this.handleRemoveRecipient}
                                            showRemove={this.state.transferRecipients.length > 1}
                                        />
                                    ))}

                                    <MuiCore.Button
                                        onClick={this.handleAddRecipient}
                                        startIcon={<Icons.Add />}
                                        style={{ marginBottom: 16, textTransform: 'none', fontFamily: 'Sofia' }}
                                    >
                                        ADD TRANSFER RECIPIENT
                                    </MuiCore.Button>

                                    <MuiCore.Box style={{ display: 'flex', gap: 12 }}>
                                        <MuiCore.Button onClick={this.handleCancelTransfer} style={{ textTransform: 'none', fontFamily: 'Sofia' }}>
                                            Cancel
                                        </MuiCore.Button>
                                        <MuiCore.Button
                                            variant="contained"
                                            onClick={this.handleSaveTransfer}
                                            style={{ backgroundColor: '#7F2062', color: '#fff', textTransform: 'none', fontFamily: 'Sofia' }}
                                        >
                                            SAVE
                                        </MuiCore.Button>
                                    </MuiCore.Box>
                                </MuiCore.Paper>
                            ) : transferActions.length > 0 ? (
                                <>
                                <WarmTransferForm warmTransferEnabled={warmTransferEnabled} accountId={accountId} />
                                <MuiCore.TableContainer>
                                    <MuiCore.Table style={{ tableLayout: 'fixed' }}>
                                        <MuiCore.TableHead>
                                            <MuiCore.TableRow>
                                                <MuiCore.TableCell style={{ fontFamily: 'Sofia-bold', width: '25%' }}>Condition</MuiCore.TableCell>
                                                <MuiCore.TableCell style={{ fontFamily: 'Sofia-bold', width: '25%' }}>Transfer To</MuiCore.TableCell>
                                                <MuiCore.TableCell style={{ fontFamily: 'Sofia-bold', width: '25%', textAlign: 'center' }}>Activated</MuiCore.TableCell>
                                                <MuiCore.TableCell style={{ fontFamily: 'Sofia-bold', width: '25%', textAlign: 'center' }}>Actions</MuiCore.TableCell>
                                            </MuiCore.TableRow>
                                        </MuiCore.TableHead>
                                        <MuiCore.TableBody>
                                            {transferActions.map((action) => (
                                                <MuiCore.TableRow key={action.aiActionId}>
                                                    <MuiCore.TableCell style={{ fontFamily: 'Sofia' }}>{action.description}</MuiCore.TableCell>
                                                    <MuiCore.TableCell style={{ fontFamily: 'Sofia' }}>
                                                        {this.parseTransferRecipients(action.parameters)}
                                                    </MuiCore.TableCell>
                                                    <MuiCore.TableCell style={{ textAlign: 'center' }}>
                                                        {action.status === 'ACTIVE' ? (
                                                            <Icons.Check style={{ color: '#4caf50' }} />
                                                        ) : (
                                                            <Icons.Close style={{ color: '#f44336' }} />
                                                        )}
                                                    </MuiCore.TableCell>
                                                    <MuiCore.TableCell style={{ textAlign: 'center' }}>
                                                        <MuiCore.IconButton 
                                                            size="small" 
                                                            onClick={(e) => this.handleMenuOpen(e, action)}
                                                        >
                                                            <Icons.MoreVert />
                                                        </MuiCore.IconButton>
                                                    </MuiCore.TableCell>
                                                </MuiCore.TableRow>
                                            ))}
                                        </MuiCore.TableBody>
                                    </MuiCore.Table>
                                </MuiCore.TableContainer>
                                </>
                            ) : (
                                <MuiCore.Typography style={{ padding: 16, fontFamily: 'Sofia', color: '#999' }}>
                                    No Information
                                </MuiCore.Typography>
                            )}
                        </MuiCore.Box>

                        {/* =============== SMS ACTIONS SECTION =============== */}
                        <MuiCore.Box className={classes.section} style={{ marginBottom: 32 }}>
                            <MuiCore.Box style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                                <Icons.Sms style={{ marginRight: 8, color: '#7F2062' }} />
                                <MuiCore.Typography variant="h6" style={{ fontFamily: 'Sofia-bold', flex: 1, color: '#7F2062' }}>
                                    SMS Actions
                                </MuiCore.Typography>
                                <MuiCore.Button
                                    variant="outlined"
                                    size="small"
                                    onClick={this.handleAddSms}
                                    startIcon={<Icons.Add />}
                                    style={{ textTransform: 'none', fontFamily: 'Sofia' }}
                                >
                                    ADD
                                </MuiCore.Button>
                            </MuiCore.Box>
                            <MuiCore.Typography variant="body2" color="textSecondary" style={{ marginBottom: 16, fontFamily: 'Sofia' }}>
                                Your Grasshopper AI can send SMS messages to callers when the requested information is available.
                            </MuiCore.Typography>

                            {this.state.showSmsForm ? (
                                <MuiCore.Paper style={{ padding: 24, marginBottom: 16 }}>
                                    <MuiCore.Typography variant="h6" style={{ marginBottom: 16, fontFamily: 'Sofia-bold' }}>
                                        {this.state.editingSms ? 'Edit SMS Action' : 'Create SMS Action'}
                                    </MuiCore.Typography>

                                    <MuiCore.TextField
                                        label="Data Name *"
                                        value={this.state.smsDataName}
                                        onChange={(e) => this.setState({ smsDataName: e.target.value, smsErrors: {} })}
                                        variant="outlined"
                                        fullWidth
                                        style={{ marginBottom: 16 }}
                                        placeholder="Enter data name"
                                        error={!!this.state.smsErrors.name}
                                        helperText={this.state.smsErrors.name || ''}
                                        inputProps={{ style: { fontFamily: 'Sofia' } }}
                                        InputLabelProps={{ style: { fontFamily: 'Sofia' } }}
                                        InputProps={{ style: { fontFamily: 'Sofia' } }}
                                    />

                                    <MuiCore.TextField
                                        label="Data Content *"
                                        value={this.state.smsDataContent}
                                        onChange={(e) => this.setState({ smsDataContent: e.target.value, smsErrors: {} })}
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        style={{ marginBottom: 16 }}
                                        placeholder="Enter data content"
                                        error={!!this.state.smsErrors.content}
                                        helperText={this.state.smsErrors.content || ''}
                                        inputProps={{ style: { fontFamily: 'Sofia' } }}
                                        InputLabelProps={{ style: { fontFamily: 'Sofia' } }}
                                        InputProps={{ style: { fontFamily: 'Sofia' } }}
                                    />

                                    <MuiCore.Box style={{ display: 'flex', gap: 12 }}>
                                        <MuiCore.Button onClick={this.handleCancelSms} style={{ textTransform: 'none', fontFamily: 'Sofia' }}>
                                            Cancel
                                        </MuiCore.Button>
                                        <MuiCore.Button
                                            variant="contained"
                                            onClick={this.handleSaveSms}
                                            style={{ backgroundColor: '#7F2062', color: '#fff', textTransform: 'none', fontFamily: 'Sofia' }}
                                        >
                                            SAVE
                                        </MuiCore.Button>
                                    </MuiCore.Box>
                                </MuiCore.Paper>
                            ) : smsActions.length > 0 ? (
                                <MuiCore.TableContainer>
                                    <MuiCore.Table style={{ tableLayout: 'fixed' }}>
                                        <MuiCore.TableHead>
                                            <MuiCore.TableRow>
                                                <MuiCore.TableCell style={{ fontFamily: 'Sofia-bold', width: '25%' }}>Data Name</MuiCore.TableCell>
                                                <MuiCore.TableCell style={{ fontFamily: 'Sofia-bold', width: '25%' }}>Data Content</MuiCore.TableCell>
                                                <MuiCore.TableCell style={{ fontFamily: 'Sofia-bold', width: '25%', textAlign: 'center' }}>Activated</MuiCore.TableCell>
                                                <MuiCore.TableCell style={{ fontFamily: 'Sofia-bold', width: '25%', textAlign: 'center' }}>Actions</MuiCore.TableCell>
                                            </MuiCore.TableRow>
                                        </MuiCore.TableHead>
                                        <MuiCore.TableBody>
                                            {smsActions.map((action) => (
                                                <MuiCore.TableRow key={action.aiActionId}>
                                                    <MuiCore.TableCell style={{ fontFamily: 'Sofia' }}>{action.actionName}</MuiCore.TableCell>
                                                    <MuiCore.TableCell style={{ fontFamily: 'Sofia' }}>{action.description}</MuiCore.TableCell>
                                                    <MuiCore.TableCell style={{ textAlign: 'center' }}>
                                                        {action.status === 'ACTIVE' ? (
                                                            <Icons.Check style={{ color: '#4caf50' }} />
                                                        ) : (
                                                            <Icons.Close style={{ color: '#f44336' }} />
                                                        )}
                                                    </MuiCore.TableCell>
                                                    <MuiCore.TableCell style={{ textAlign: 'center' }}>
                                                        <MuiCore.IconButton 
                                                            size="small" 
                                                            onClick={(e) => this.handleMenuOpen(e, action)}
                                                        >
                                                            <Icons.MoreVert />
                                                        </MuiCore.IconButton>
                                                    </MuiCore.TableCell>
                                                </MuiCore.TableRow>
                                            ))}
                                        </MuiCore.TableBody>
                                    </MuiCore.Table>
                                </MuiCore.TableContainer>
                            ) : (
                                <MuiCore.Typography style={{ padding: 16, fontFamily: 'Sofia', color: '#999' }}>
                                    No Information
                                </MuiCore.Typography>
                            )}
                        </MuiCore.Box>

                        {/* =============== EMAIL ACTIONS SECTION =============== */}
                        <MuiCore.Box className={classes.section}>
                            <MuiCore.Box style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                                <Icons.Email style={{ marginRight: 8, color: '#7F2062' }} />
                                <MuiCore.Typography variant="h6" style={{ fontFamily: 'Sofia-bold', flex: 1, color: '#7F2062' }}>
                                    Email Actions
                                </MuiCore.Typography>
                                <MuiCore.Button
                                    variant="outlined"
                                    size="small"
                                    onClick={this.handleAddEmail}
                                    startIcon={<Icons.Add />}
                                    style={{ textTransform: 'none', fontFamily: 'Sofia' }}
                                >
                                    ADD
                                </MuiCore.Button>
                            </MuiCore.Box>
                            <MuiCore.Typography variant="body2" color="textSecondary" style={{ marginBottom: 16, fontFamily: 'Sofia' }}>
                                Your Grasshopper AI can send email messages to callers when the requested information is available.
                            </MuiCore.Typography>

                            {this.state.showEmailForm ? (
                                <MuiCore.Paper style={{ padding: 24, marginBottom: 16 }}>
                                    <MuiCore.Typography variant="h6" style={{ marginBottom: 16, fontFamily: 'Sofia-bold' }}>
                                        {this.state.editingEmail ? 'Edit Email Action' : 'Create Email Action'}
                                    </MuiCore.Typography>

                                    <MuiCore.TextField
                                        label="Email Name *"
                                        value={this.state.emailName}
                                        onChange={(e) => this.setState({ emailName: e.target.value, emailErrors: {} })}
                                        variant="outlined"
                                        fullWidth
                                        style={{ marginBottom: 16 }}
                                        placeholder="Enter email name"
                                        error={!!this.state.emailErrors.name}
                                        helperText={this.state.emailErrors.name || ''}
                                        inputProps={{ style: { fontFamily: 'Sofia' } }}
                                        InputLabelProps={{ style: { fontFamily: 'Sofia' } }}
                                        InputProps={{ style: { fontFamily: 'Sofia' } }}
                                    />

                                    <MuiCore.TextField
                                        label="Subject *"
                                        value={this.state.emailSubject}
                                        onChange={(e) => this.setState({ emailSubject: e.target.value, emailErrors: {} })}
                                        variant="outlined"
                                        fullWidth
                                        style={{ marginBottom: 16 }}
                                        placeholder="Enter email subject"
                                        error={!!this.state.emailErrors.subject}
                                        helperText={this.state.emailErrors.subject || ''}
                                        inputProps={{ style: { fontFamily: 'Sofia' } }}
                                        InputLabelProps={{ style: { fontFamily: 'Sofia' } }}
                                        InputProps={{ style: { fontFamily: 'Sofia' } }}
                                    />

                                    <MuiCore.TextField
                                        label="Body *"
                                        value={this.state.emailBody}
                                        onChange={(e) => this.setState({ emailBody: e.target.value, emailErrors: {} })}
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={5}
                                        style={{ marginBottom: 16 }}
                                        placeholder="Enter email body"
                                        error={!!this.state.emailErrors.body}
                                        helperText={this.state.emailErrors.body || ''}
                                        inputProps={{ style: { fontFamily: 'Sofia' } }}
                                        InputLabelProps={{ style: { fontFamily: 'Sofia' } }}
                                        InputProps={{ style: { fontFamily: 'Sofia' } }}
                                    />

                                    <MuiCore.Box style={{ display: 'flex', gap: 12 }}>
                                        <MuiCore.Button onClick={this.handleCancelEmail} style={{ textTransform: 'none', fontFamily: 'Sofia' }}>
                                            Cancel
                                        </MuiCore.Button>
                                        <MuiCore.Button
                                            variant="contained"
                                            onClick={this.handleSaveEmail}
                                            style={{ backgroundColor: '#7F2062', color: '#fff', textTransform: 'none', fontFamily: 'Sofia' }}
                                        >
                                            SAVE
                                        </MuiCore.Button>
                                    </MuiCore.Box>
                                </MuiCore.Paper>
                            ) : emailActions.length > 0 ? (
                                <MuiCore.TableContainer>
                                    <MuiCore.Table style={{ tableLayout: 'fixed' }}>
                                        <MuiCore.TableHead>
                                            <MuiCore.TableRow>
                                                <MuiCore.TableCell style={{ fontFamily: 'Sofia-bold', width: '25%' }}>Email Name</MuiCore.TableCell>
                                                <MuiCore.TableCell style={{ fontFamily: 'Sofia-bold', width: '25%' }}>Subject</MuiCore.TableCell>
                                                <MuiCore.TableCell style={{ fontFamily: 'Sofia-bold', width: '25%', textAlign: 'center' }}>Activated</MuiCore.TableCell>
                                                <MuiCore.TableCell style={{ fontFamily: 'Sofia-bold', width: '25%', textAlign: 'center' }}>Actions</MuiCore.TableCell>
                                            </MuiCore.TableRow>
                                        </MuiCore.TableHead>
                                        <MuiCore.TableBody>
                                            {emailActions.map((action) => {
                                                let subject = '';
                                                try {
                                                    const parsed = JSON.parse(action.parameters);
                                                    subject = parsed.subject || '';
                                                } catch (e) {}
                                                
                                                return (
                                                    <MuiCore.TableRow key={action.aiActionId}>
                                                        <MuiCore.TableCell style={{ fontFamily: 'Sofia' }}>{action.actionName}</MuiCore.TableCell>
                                                        <MuiCore.TableCell style={{ fontFamily: 'Sofia' }}>{subject}</MuiCore.TableCell>
                                                        <MuiCore.TableCell style={{ textAlign: 'center' }}>
                                                            {action.status === 'ACTIVE' ? (
                                                                <Icons.Check style={{ color: '#4caf50' }} />
                                                            ) : (
                                                                <Icons.Close style={{ color: '#f44336' }} />
                                                            )}
                                                        </MuiCore.TableCell>
                                                        <MuiCore.TableCell style={{ textAlign: 'center' }}>
                                                            <MuiCore.IconButton 
                                                                size="small" 
                                                                onClick={(e) => this.handleMenuOpen(e, action)}
                                                            >
                                                                <Icons.MoreVert />
                                                            </MuiCore.IconButton>
                                                        </MuiCore.TableCell>
                                                    </MuiCore.TableRow>
                                                );
                                            })}
                                        </MuiCore.TableBody>
                                    </MuiCore.Table>
                                </MuiCore.TableContainer>
                            ) : (
                                <MuiCore.Typography style={{ padding: 16, fontFamily: 'Sofia', color: '#999' }}>
                                    No Information
                                </MuiCore.Typography>
                            )}
                        </MuiCore.Box>
                    </>
                )}

                {/* Context Menu for Actions */}
                <MuiCore.Menu
                    anchorEl={this.state.anchorEl}
                    open={Boolean(this.state.anchorEl)}
                    onClose={this.handleMenuClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <MuiCore.MenuItem onClick={this.handleMenuEdit} style={{ fontFamily: 'Sofia' }}>
                        <Icons.Edit style={{ marginRight: 8, fontSize: 20 }} />
                        Edit
                    </MuiCore.MenuItem>
                    {this.state.menuAction && (this.state.menuAction.status === 'DISABLE' || this.state.menuAction.status === 'NEW') && (
                        <MuiCore.MenuItem onClick={this.handleMenuToggle} style={{ fontFamily: 'Sofia' }}>
                            <Icons.CheckCircle style={{ marginRight: 8, fontSize: 20, color: '#4caf50' }} />
                            Activate
                        </MuiCore.MenuItem>
                    )}
                    {this.state.menuAction && this.state.menuAction.status === 'ACTIVE' && (
                        <MuiCore.MenuItem onClick={this.handleMenuToggle} style={{ fontFamily: 'Sofia' }}>
                            <Icons.Cancel style={{ marginRight: 8, fontSize: 20, color: '#f44336' }} />
                            Disable
                        </MuiCore.MenuItem>
                    )}
                </MuiCore.Menu>
            </MuiCore.Box>
        );
    }
}

export default AIActionsCard;
